import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type Dialect = 'hmonglus' | 'moob';
export type Voice = 'adult' | 'child';
export type BaseLanguage = 'en' | 'fr' | 'es' | 'de' | 'it' | 'vi' | 'lo' | 'th';

type Preferences = {
  dialect: Dialect;
  voice: Voice;
  baseLanguage: BaseLanguage;
  setDialect: (value: Dialect) => void;
  setVoice: (value: Voice) => void;
  setBaseLanguage: (value: BaseLanguage) => void;
};

const STORAGE_KEY = 'hmonglus:web:preferences';
const defaults = { dialect: 'hmonglus' as Dialect, voice: 'child' as Voice, baseLanguage: 'en' as BaseLanguage };
const PreferencesContext = createContext<Preferences | null>(null);

function isDialect(value: unknown): value is Dialect {
  return value === 'hmonglus' || value === 'moob';
}

function readPreferences(): typeof defaults {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as Partial<typeof defaults>;
    const dialect = isDialect(stored.dialect) ? stored.dialect : defaults.dialect;
    return { ...defaults, ...stored, dialect };
  } catch {
    return defaults;
  }
}

export function LearnerPreferencesProvider({ children }: React.PropsWithChildren) {
  const [value, setValue] = useState(readPreferences);
  const update = useCallback((next: typeof defaults) => {
    setValue(next);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY, newValue: JSON.stringify(next) }));
  }, []);
  const context = useMemo<Preferences>(
    () => ({
      ...value,
      setDialect: (dialect) => update({ ...value, dialect }),
      setVoice: (voice) => update({ ...value, voice }),
      setBaseLanguage: (baseLanguage) => update({ ...value, baseLanguage }),
    }),
    [update, value],
  );
  return <PreferencesContext.Provider value={context}>{children}</PreferencesContext.Provider>;
}

export function useLearnerPreferences() {
  const value = useContext(PreferencesContext);
  if (!value) throw new Error('useLearnerPreferences must be used inside LearnerPreferencesProvider');
  return value;
}
